import {HttpResponse} from '@angular/common/http';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AnalyticAgentApiService, AnalyticAgentReportType} from '@npck/af-frontend-api';
import {downloadFromBlobResponse, Spinner} from '@npck/libs/commons';
import {map, mergeMap} from 'rxjs';
import {delay, finalize} from 'rxjs/operators';

@Component({
    selector: 'af-analytic-agent-report-dialog',
    standalone: false,
    templateUrl: './analytic-agent-report-dialog.component.html',
})
export class AnalyticAgentReportDialogComponent implements OnInit, OnDestroy {
    Spinner = Spinner;
    form: FormGroup<AnalyticAgentReportForm>;
    isReportGenerationStarted: boolean;
    downloadOrRetryLabel: string;
    reportFile: HttpResponse<Blob>;

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: AnalyticAgentReportDialogData,
        public dialogRef: MatDialogRef<AnalyticAgentReportDialogComponent>,
        private formBuilder: FormBuilder,
        private analyticAgentApiService: AnalyticAgentApiService,
    ) {
        this.form = this.formBuilder.group({
            reportType: new FormControl(null, [Validators.required])
        });
    }

    ngOnInit(): void {
        this.isReportGenerationStarted = false;
    }

    ngOnDestroy(): void {
        this.isReportGenerationStarted = false;
        this.reportFile = null;
    }

    generateAndWaitForAnalyticAgentReport(): void {
        this.Spinner.show('analyticAgentReportLoading');
        if (!this.isReportGenerationStarted) {
            this.isReportGenerationStarted = true;
        }

        const reportType = this.form.controls.reportType.value;
        this.analyticAgentApiService.generateIncidentReport(
            this.dialogData.incidentId,
            reportType
        ).pipe(
            mergeMap(result => this.analyticAgentApiService.downloadIncidentReport(result.id, 'response')),
            map((reportFile) => {
                this.reportFile = reportFile;
            }),
            delay(500),
            finalize(() => this.Spinner.hide('analyticAgentReportLoading'))
        ).subscribe({
            next: () => {
                this.downloadOrRetryLabel = 'i18n_af_buttonAnalyticAgentDownloadReport#' + reportType;
            },
            error: err => {
                this.reportFile = null;
                this.downloadOrRetryLabel = 'i18n_af_buttonAnalyticAgentRetry';
            }
        });
    }

    downloadOrRetry(): void {
        if (this.reportFile) {
            const reportType = this.form.controls.reportType.value;
            switch (reportType) {
                case AnalyticAgentReportType.PDF: downloadFromBlobResponse(this.reportFile); break;
                case AnalyticAgentReportType.HTML: this.openFileInNewTab(this.reportFile); break;
            }
            this.reportFile = null;
            this.dialogRef.close();
            return;
        }

        this.reportFile = null;
        this.generateAndWaitForAnalyticAgentReport();
    }

    private openFileInNewTab(response: HttpResponse<Blob>): void {
        const file = new Blob([response.body], {type: 'text/html'});
        const fileURL = URL.createObjectURL(file);

        // Open the local reference in a blank target tab
        window.open(fileURL, '_blank');
    }
}

export interface AnalyticAgentReportDialogData {
    incidentId: string;
}

interface AnalyticAgentReportForm {
    reportType: FormControl<AnalyticAgentReportType>;
}
